import { Fragment } from 'react'
import { Link, NavLink, useLocation } from 'react-router-dom'
import {
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  Transition,
} from '@headlessui/react'
import {
  PlusIcon,
  HomeIcon,
  CalendarIcon,
  ArrowRightEndOnRectangleIcon,
} from '@heroicons/react/20/solid'
import { BuildingOfficeIcon, UserIcon } from '@heroicons/react/24/outline'

import {
  useAuth,
  useCurrentWorkspace,
  usePermissions,
  useUserProfile,
} from '../../store/hooks'
import { WidthWrap } from './WidthWrap'
import { WorkspaceType } from '../../lib/db/db.types'

export const SceneHeader = () => {
  const location = useLocation()
  const { workspace, myWorkspaces, onSetCurrentWorkspace } =
    useCurrentWorkspace()
  const { canManageEvents, canManageTeam, canManageDocuments } =
    usePermissions()
  const { userId, logoutDisabled, onLogout } = useAuth()
  const { profile } = useUserProfile()

  return (
    <header className="bg-white shadow">
      <WidthWrap>
        <div className="flex h-16 justify-between items-center">
          <div>
            {/* Brand */}
            <Link to="/" className="text-xl font-bold text-gray-900">
              My Admin Pal (beta)
            </Link>
          </div>

          {/* Combined Profile & Business Menu */}
          <Menu as="div" className="relative">
            <MenuButton className="flex items-center rounded-full bg-gray-100 p-2 text-sm focus:outline-none hover:bg-gray-200">
              <span className="sr-only">Open menu</span>
              <div className="h-8 w-8 rounded-full bg-blue-600 flex items-center justify-center text-white">
                {profile?.name
                  ?.split(' ')
                  .map((n) => n[0])
                  .join('')}
              </div>
            </MenuButton>
            <Transition
              as={Fragment}
              enter="transition ease-out duration-100"
              enterFrom="transform opacity-0 scale-95"
              enterTo="transform opacity-100 scale-100"
              leave="transition ease-in duration-75"
              leaveFrom="transform opacity-100 scale-100"
              leaveTo="transform opacity-0 scale-95"
            >
              <MenuItems className="absolute right-0 z-10 mt-2 w-72 origin-top-right rounded-md bg-white py-1 shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                <MenuItem>
                  <NavLink
                    to="/"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm text-gray-700 ${
                        isActive ? 'bg-blue-100' : 'hover:bg-blue-50'
                      } flex items-center`
                    }
                  >
                    <HomeIcon className="h-4 w-4 mr-2" />
                    Dashboard
                  </NavLink>
                </MenuItem>

                <MenuItem>
                  <NavLink
                    to={`/calendar/`}
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm text-gray-700 ${
                        isActive ? 'bg-blue-100' : 'hover:bg-blue-50'
                      } flex items-center`
                    }
                  >
                    <CalendarIcon className="h-4 w-4 mr-2" />
                    Combined Calendar
                  </NavLink>
                </MenuItem>

                <MenuItem>
                  <NavLink
                    to="/profile/"
                    className={({ isActive }) =>
                      `block px-4 py-2 text-sm text-gray-700 ${
                        isActive ? 'bg-blue-100' : 'hover:bg-blue-50'
                      } flex items-center`
                    }
                  >
                    <UserIcon className="h-4 w-4 mr-2" />
                    My Profile
                  </NavLink>
                </MenuItem>

                <div className="border-t border-gray-100 my-1" />

                {/* Personal Section */}
                {!!userId && (
                  <>
                    <div className="px-4 py-2 text-xs font-medium text-gray-500">
                      Personal
                    </div>

                    <MenuItem>
                      <NavLink
                        to={`/workspace/${userId}`}
                        onClick={() => onSetCurrentWorkspace(userId)}
                        className={({ isActive }) =>
                          `block px-4 py-2 text-sm text-gray-700 ${
                            isActive ? 'bg-blue-100' : 'hover:bg-blue-50'
                          } flex items-center`
                        }
                      >
                        <UserIcon className="h-4 w-4 mr-2" />
                        Personal Workspace
                      </NavLink>
                    </MenuItem>

                    <div className="border-t border-gray-100 my-1" />
                  </>
                )}

                {/* Businesses Section */}
                <div className="px-4 py-2 text-xs font-medium text-gray-500">
                  Business
                </div>
                {myWorkspaces.map((w) => {
                  if (w.spaceType !== WorkspaceType.BUSINESS) return null
                  return (
                    <MenuItem key={w.id}>
                      <NavLink
                        to={`/workspace/${w.id}/`}
                        onClick={() => onSetCurrentWorkspace(w.id)}
                        className={({ isActive }) =>
                          `block w-full px-4 py-2 text-left ${
                            isActive ? 'bg-blue-100' : 'hover:bg-blue-50'
                          }`
                        }
                      >
                        <div className="flex items-center">
                          <BuildingOfficeIcon className="h-5 w-5 text-gray-400 mr-2" />
                          <div>
                            <div className="text-sm text-gray-700">
                              {w.name}
                            </div>
                            <div className="text-xs text-gray-500">
                              {w.business.business.companyNumber}
                            </div>
                          </div>
                        </div>
                      </NavLink>
                    </MenuItem>
                  )
                })}

                <MenuItem>
                  <NavLink
                    to="/new-business"
                    className={({ isActive }) =>
                      `px-4 py-2 text-sm text-gray-700 flex items-center ${
                        isActive ? 'bg-blue-100' : 'hover:bg-blue-50'
                      }`
                    }
                  >
                    <PlusIcon className="h-4 w-4 mr-2" />
                    Add new business
                  </NavLink>
                </MenuItem>

                <div className="border-t border-gray-100 my-1" />

                {/* Logout */}
                <MenuItem>
                  <button
                    className="w-full px-4 py-2 text-left text-sm text-gray-700 hover:bg-gray-100 flex items-center"
                    disabled={logoutDisabled}
                    onClick={onLogout}
                  >
                    <ArrowRightEndOnRectangleIcon className="h-4 w-4 mr-2" />
                    Logout
                  </button>
                </MenuItem>
              </MenuItems>
            </Transition>
          </Menu>
        </div>

        {/* Conditional Navigation Bars */}
        {!!workspace && location.pathname.startsWith('/workspace/') && (
          <div className="border-t py-4 space-y-2">
            <div className="text-md font-bold">{workspace.name}</div>

            <div className="flex items-center space-x-6">
              <NavLink
                to={`/workspace/${workspace.id}/calendar/`}
                className={({ isActive }) =>
                  `text-sm ${
                    isActive
                      ? 'text-blue-600 hover:text-blue-900'
                      : 'text-gray-600 hover:text-gray-900'
                  }`
                }
              >
                Calendar
              </NavLink>

              {canManageEvents && (
                <NavLink
                  to={`/workspace/${workspace.id}/manage-events/`}
                  className={({ isActive }) =>
                    `text-sm ${
                      isActive
                        ? 'text-blue-600 hover:text-blue-900'
                        : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  Manage events
                </NavLink>
              )}

              {canManageDocuments && (
                <NavLink
                  to={`/workspace/${workspace.id}/documents/`}
                  className={({ isActive }) =>
                    `text-sm ${
                      isActive
                        ? 'text-blue-600 hover:text-blue-900'
                        : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  Documents
                </NavLink>
              )}

              {workspace.spaceType === WorkspaceType.BUSINESS && (
                <NavLink
                  to={`/workspace/${workspace.id}/details/`}
                  className={({ isActive }) =>
                    `text-sm ${
                      isActive
                        ? 'text-blue-600 hover:text-blue-900'
                        : 'text-gray-600 hover:text-gray-900'
                    }`
                  }
                >
                  Business details
                </NavLink>
              )}

              {workspace.spaceType === WorkspaceType.BUSINESS &&
                canManageTeam && (
                  <NavLink
                    to={`/workspace/${workspace.id}/team/`}
                    className={({ isActive }) =>
                      `text-sm ${
                        isActive
                          ? 'text-blue-600 hover:text-blue-900'
                          : 'text-gray-600 hover:text-gray-900'
                      }`
                    }
                  >
                    Manage Team
                  </NavLink>
                )}
            </div>
          </div>
        )}
      </WidthWrap>
    </header>
  )
}
